import React from 'react'
import { useI18n } from '../i18n/I18nContext'
import { ComparisonSection } from '../components/landing/ComparisonSection'
import { ContactSection } from '../components/landing/ContactSection'
import { FinalCtaSection } from '../components/landing/FinalCtaSection'
import { LandingHero } from '../components/landing/LandingHero'
import { LandingNav } from '../components/landing/LandingNav'
import { PricingSection } from '../components/landing/PricingSection'
import { ProductOverviewSection } from '../components/landing/ProductOverviewSection'
import { RoiSection } from '../components/landing/RoiSection'
import { ScreensSection } from '../components/landing/ScreensSection'
import { SeoHead } from '../components/landing/SeoHead'
import { ComparisonListSection, FaqSection, FeatureGrid, LandingFooter, ProblemSolutionSection, UseCaseGrid } from '../components/landing/SalesLandingSections'
import {
  comparisonContent,
  comparisonFeatures,
  contactContent,
  content,
  competitorGaps,
  screenCards,
  type LandingLocale,
} from '../components/landing/landingContent'
import { nexusLandingContent } from '../components/landing/nexusLandingContent'
import {
  brandTitleForLocale,
  buildLocalizedStructuredData,
  runtimeCanonicalUrl,
  HTML_DIR,
  hreflangAlternates,
  localeSwitchDestination,
  marketingPathForLocale,
  OG_LOCALE,
  ogLocaleAlternates,
} from '../components/landing/marketingSeoUrls'
import type { PublicMarketingLocale } from '../components/landing/marketingPages'

// Locale-aware homepage metadata. Mirrors HOME_META in
// server/src/seo/marketingSeo.ts so the hydrated head matches the initial HTML.
const HOME_META: Record<PublicMarketingLocale, { title: string; description: string }> = {
  en: {
    title: 'Free Workforce Management Software | NexusWorkforce360',
    description:
      'Run workers, jobs, schedules, locations, geofenced attendance, compliance, timesheets, and approvals on the $0 core Workforce Network OS. No per-worker pricing for core operations.',
  },
  ar: {
    title: 'برنامج مجاني لإدارة القوى العاملة | رقيب 360',
    description:
      'شغّل العمال والوظائف والجداول والمواقع والحضور الجغرافي والامتثال وجداول الساعات والاعتمادات بقيمة 0 دولار دون تسعير لكل عامل للعمليات الأساسية.',
  },
  fr: {
    title: 'Logiciel gratuit de gestion des effectifs | NexusWorkforce360',
    description:
      'Pilotez travailleurs, jobs, plannings, sites, présence géolocalisée, conformité, feuilles de temps et validations sur le Workforce Network OS essentiel à 0 $.',
  },
}

const localizedOverview = {
  en: {
    title: 'More than a free time clock. A Workforce Network OS.',
    copy: 'NexusWorkforce360 connects the full lifecycle—Workforce Demand → Supply → Allocation → Compliance → Execution → Settlement—for companies, staffing agencies, subcontractors, temporary workers, and field teams. Scheduling, jobs, attendance, geofencing, approvals, compliance, and payroll exports stay connected instead of becoming isolated tools.',
  },
  fr: {
    title: 'Plus qu’une pointeuse gratuite : un Workforce Network OS.',
    copy: 'NexusWorkforce360 relie le cycle complet — Demande → Offre → Allocation → Conformité → Exécution → Règlement — pour entreprises, agences, sous-traitants, temporaires et équipes terrain. Plannings, jobs, présence, géofencing, validations, conformité et exports paie restent connectés.',
  },
  ar: {
    title: 'أكثر من ساعة حضور مجانية: نظام تشغيل لشبكة القوى العاملة.',
    copy: 'يربط رقيب 360 دورة العمل كاملة: الطلب ← التوفير ← التوزيع ← الامتثال ← التنفيذ ← التسوية، للشركات والوكالات والمقاولين والعمال. تبقى الجداول والوظائف والحضور والنطاق الجغرافي والاعتمادات والامتثال وتصديرات الرواتب مترابطة.',
  },
  tr: {
    title: 'A unified workforce supply chain platform',
    copy: 'NexusWorkforce360 gives agencies and companies one operational process across internal employees, staffing agencies, subcontractors, temporary workers, and field teams. Attendance, geofencing, offline punches, scheduling, approvals, billing validation, compliance, skills progression, payroll exports, and reliability signals sit inside a larger Workforce Network OS.',
  },
}

const localizedSalesDeckPromo = {
  en: {
    eyebrow: 'Explore Interactive Presentations',
    title: 'NexusWorkforce360 Sales Decks',
    featured: 'FEATURED',
    interactiveTitle: 'Interactive Sales Deck',
    interactiveCopy: 'Live browser-based presentation with interactive simulators and real-time demonstrations.',
    interactiveCta: 'Launch Presentation ->',
    decksTitle: 'Industry-Specific Decks',
    decksCopy: 'Printable, segment-specific decks for recruitment, construction, food & beverage, and security.',
    decksCta: 'View All Decks ->',
  },
  fr: {
    eyebrow: 'Explorer les présentations interactives',
    title: 'Présentations commerciales NexusWorkforce360',
    featured: 'À LA UNE',
    interactiveTitle: 'Présentation commerciale interactive',
    interactiveCopy: 'Présentation live dans le navigateur avec simulateurs interactifs et démonstrations en temps réel.',
    interactiveCta: 'Lancer la présentation ->',
    decksTitle: 'Présentations par secteur',
    decksCopy: 'Présentations imprimables et segmentées pour recrutement, construction, restauration et sécurité.',
    decksCta: 'Voir toutes les présentations ->',
  },
  ar: {
    eyebrow: 'استكشف العروض التفاعلية',
    title: 'عروض NexusWorkforce360 التجارية',
    featured: 'مميز',
    interactiveTitle: 'عرض تجاري تفاعلي',
    interactiveCopy: 'عرض مباشر في المتصفح مع محاكيات تفاعلية وتوضيحات فورية.',
    interactiveCta: 'تشغيل العرض ->',
    decksTitle: 'عروض مخصصة للقطاعات',
    decksCopy: 'عروض قابلة للطباعة ومخصصة للتوظيف والمقاولات والأغذية والمشروبات والأمن.',
    decksCta: 'عرض كل العروض ->',
  },
  tr: {
    eyebrow: 'Explore Interactive Presentations',
    title: 'NexusWorkforce360 Sales Decks',
    featured: 'FEATURED',
    interactiveTitle: 'Interactive Sales Deck',
    interactiveCopy: 'Live browser-based presentation with interactive simulators and real-time demonstrations.',
    interactiveCta: 'Launch Presentation ->',
    decksTitle: 'Industry-Specific Decks',
    decksCopy: 'Printable, segment-specific decks for recruitment, construction, food & beverage, and security.',
    decksCta: 'View All Decks ->',
  },
}

const freeSeoLinks: Record<PublicMarketingLocale, Array<[string, string]>> = {
  en: [
    ['Free Workforce Management Software', 'free-workforce-management-software'],
    ['Free Staffing Agency Software', 'staffing-agency-software'],
    ['Free Construction Workforce Management', 'construction-workforce-management'],
    ['Free Security Workforce Management', 'security-workforce-management'],
    ['Free Hospitality Workforce Management', 'hospitality-workforce-management'],
    ['Free Geofenced Attendance', 'geofenced-attendance-tracking'],
    ['Free Field Worker Time Tracking', 'field-worker-time-tracking'],
  ],
  fr: [
    ['Logiciel gratuit de gestion des effectifs', 'free-workforce-management-software'],
    ['Logiciel gratuit pour agences de staffing', 'staffing-agency-software'],
    ['Gestion gratuite des effectifs construction', 'construction-workforce-management'],
    ['Gestion gratuite des équipes de sécurité', 'security-workforce-management'],
    ['Gestion gratuite des effectifs hospitality', 'hospitality-workforce-management'],
    ['Présence géolocalisée gratuite', 'geofenced-attendance-tracking'],
    ['Suivi gratuit du temps des équipes terrain', 'field-worker-time-tracking'],
  ],
  ar: [
    ['برنامج مجاني لإدارة القوى العاملة', 'free-workforce-management-software'],
    ['برنامج مجاني لإدارة شركات التوظيف', 'staffing-agency-software'],
    ['إدارة مجانية لعمال البناء', 'construction-workforce-management'],
    ['إدارة مجانية لفرق الأمن', 'security-workforce-management'],
    ['إدارة مجانية لعمال الضيافة', 'hospitality-workforce-management'],
    ['حضور مجاني بالنطاق الجغرافي', 'geofenced-attendance-tracking'],
    ['تتبع مجاني لوقت العمال الميدانيين', 'field-worker-time-tracking'],
  ],
}

export const NexusLandingPage: React.FC = () => {
  const { locale } = useI18n()
  const landingLocale = locale === 'tr' ? 'en' : locale
  const seoLocale = landingLocale as PublicMarketingLocale
  const homeMeta = HOME_META[seoLocale]
  const homeTitle = brandTitleForLocale(homeMeta.title, seoLocale)
  const homeCanonical = runtimeCanonicalUrl('home', null, seoLocale)
  const handleLocaleSwitch = (next: LandingLocale) => {
    if (typeof window !== 'undefined') {
      const target: PublicMarketingLocale = next === 'tr' ? 'en' : next
      window.location.href = localeSwitchDestination('home', null, target)
    }
  }
  const legacyCopy = content[landingLocale]
  const salesCopy = nexusLandingContent[landingLocale]
  const overviewCopy = localizedOverview[landingLocale]
  const salesDeckPromo = localizedSalesDeckPromo[landingLocale]
  const heroCopy = {
    ...legacyCopy,
    nav: salesCopy.nav,
    getStarted: salesCopy.hero.primaryCta.label,
    heroEyebrow: salesCopy.hero.eyebrow,
    heroTitle: salesCopy.hero.title,
    heroCopy: salesCopy.hero.copy,
    heroProof: salesCopy.hero.proof,
    heroPrimaryCta: salesCopy.hero.primaryCta.label,
    heroSecondaryCta: salesCopy.hero.secondaryCta.label,
    heroSecondaryHref: salesCopy.hero.secondaryCta.href,
  }

  return (
    <div className="landing-page">
      <SeoHead
        title={homeTitle}
        description={homeMeta.description}
        canonicalUrl={homeCanonical}
        ogTitle={homeTitle}
        ogDescription={homeMeta.description}
        ogUrl={homeCanonical}
        htmlLang={seoLocale}
        dir={HTML_DIR[seoLocale]}
        ogLocale={OG_LOCALE[seoLocale]}
        ogLocaleAlternates={ogLocaleAlternates('home', null, seoLocale)}
        alternates={hreflangAlternates('home', null)}
        jsonLd={buildLocalizedStructuredData({
          kind: 'home',
          slug: null,
          locale: seoLocale,
          description: homeMeta.description,
        })}
      />
      <LandingNav
        copy={salesCopy}
        appHref="/login"
        locale={landingLocale}
        onLocaleChange={handleLocaleSwitch}
        navAnchors={['#platform', '#network-os', '#comparison', '#pricing', '#screens', '#contact']}
        showRelatedPages={false}
      />

      <main id="top">
        <LandingHero copy={heroCopy} appHref={salesCopy.hero.primaryCta.href} />
        <ProductOverviewSection copy={{
          ...legacyCopy,
          overviewTitle: overviewCopy.title,
          overviewCopy: overviewCopy.copy,
        }} />
        <RoiSection content={salesCopy.roi} />
        <ProblemSolutionSection content={salesCopy.problemSolution} />
        <ComparisonListSection content={salesCopy.comparison} />
        <FeatureGrid title={salesCopy.featuresTitle} features={salesCopy.features} />
        <UseCaseGrid title={salesCopy.useCasesTitle} useCases={salesCopy.useCases} />
        <section className="landing-section landing-section--muted" id="free-workforce-solutions">
            <div className="container">
              <div className="landing-section-heading">
                <p className="landing-eyebrow">{seoLocale === 'ar' ? 'حلول مجانية' : seoLocale === 'fr' ? 'Solutions gratuites' : 'Free workforce solutions'}</p>
                <h2>{seoLocale === 'ar' ? 'استكشف مسارات مجانية لإدارة القوى العاملة' : seoLocale === 'fr' ? 'Explorez les workflows gratuits de gestion des effectifs' : 'Explore free workforce management workflows'}</h2>
              </div>
              <div className="landing-related-links">
                {freeSeoLinks[seoLocale].map(([label, slug]) => <a key={slug} href={marketingPathForLocale(slug, seoLocale)}>{label}</a>)}
              </div>
            </div>
          </section>
        <ComparisonSection
          copy={comparisonContent[landingLocale]}
          features={comparisonFeatures[landingLocale]}
          gaps={competitorGaps[landingLocale]}
        />
        <PricingSection copy={salesCopy.pricing} />
        <ScreensSection copy={legacyCopy} cards={screenCards[landingLocale]} />

        {seoLocale !== 'ar' && <section className="landing-section" id="sales-decks" style={{ backgroundColor: 'rgba(15, 23, 42, 0.5)' }}>
          <div className="container">
            <div className="landing-section-heading">
              <p className="landing-eyebrow">{salesDeckPromo.eyebrow}</p>
              <h2>{salesDeckPromo.title}</h2>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem', marginTop: '2rem' }}>
              <a href="/sales-decks/interactive" style={{ padding: '1.5rem', borderRadius: '0.75rem', border: '2px solid rgb(16, 185, 129)', backgroundColor: 'rgba(16, 185, 129, 0.1)', textDecoration: 'none', color: 'inherit', transition: 'all 0.3s ease', cursor: 'pointer' }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(16, 185, 129, 0.2)'; e.currentTarget.style.borderColor = 'rgb(5, 150, 105)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'rgba(16, 185, 129, 0.1)'; e.currentTarget.style.borderColor = 'rgb(16, 185, 129)'; }}>
                <span style={{ display: 'inline-block', backgroundColor: 'rgb(16, 185, 129)', color: 'white', padding: '0.25rem 0.75rem', borderRadius: '0.25rem', fontSize: '0.75rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>{salesDeckPromo.featured}</span>
                <h3 style={{ fontSize: '1.125rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>{salesDeckPromo.interactiveTitle}</h3>
                <p style={{ fontSize: '0.875rem', color: 'rgba(148, 163, 184, 1)', marginBottom: '1rem' }}>{salesDeckPromo.interactiveCopy}</p>
                <span style={{ color: 'rgb(16, 185, 129)', fontWeight: '600' }}>{salesDeckPromo.interactiveCta}</span>
              </a>
              <a href="/sales-decks" style={{ padding: '1.5rem', borderRadius: '0.75rem', border: '2px solid rgb(71, 85, 105)', backgroundColor: 'rgba(71, 85, 105, 0.1)', textDecoration: 'none', color: 'inherit', transition: 'all 0.3s ease', cursor: 'pointer' }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(71, 85, 105, 0.2)'; e.currentTarget.style.borderColor = 'rgb(100, 116, 139)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'rgba(71, 85, 105, 0.1)'; e.currentTarget.style.borderColor = 'rgb(71, 85, 105)'; }}>
                <h3 style={{ fontSize: '1.125rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>{salesDeckPromo.decksTitle}</h3>
                <p style={{ fontSize: '0.875rem', color: 'rgba(148, 163, 184, 1)', marginBottom: '1rem' }}>{salesDeckPromo.decksCopy}</p>
                <span style={{ color: 'rgb(100, 116, 139)', fontWeight: '600' }}>{salesDeckPromo.decksCta}</span>
              </a>
            </div>
          </div>
        </section>}

        <FaqSection eyebrow={salesCopy.faqEyebrow} title={salesCopy.faqTitle} items={salesCopy.faq} />
        <ContactSection copy={contactContent[landingLocale]} />
        <FinalCtaSection
          copy={{
            finalTitle: salesCopy.finalTitle,
            finalCopy: salesCopy.finalCopy,
            finalCta: salesCopy.finalCta.label,
          }}
          appHref={salesCopy.finalCta.href}
        />
      </main>
      <LandingFooter content={salesCopy.footer} />
    </div>
  )
}
